import * as React from "react";
import { NavLink, Outlet, useLocation, useMatches, Link, useNavigate } from "react-router";
import { ChevronDown, LogOut, Menu as MenuIcon, X, Building2, ShieldAlert, Moon, Sun } from "lucide-react";
import { Badge, Menu, MenuContent, MenuItem, MenuLabel, MenuSeparator, MenuTrigger, cn } from "@cm/ui";
import { useSession } from "../lib/session";
import type { NavGroup, NavItem } from "./nav";
import { BrandLogo } from "../components/BrandLogo";
import { useBrand } from "../lib/brand";

const ITEM_BASE = "relative flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-colors focus-visible:outline-2 focus-visible:outline-sidebar-indicator";
const ITEM_ACTIVE = "bg-sidebar-active font-medium text-sidebar-active-fg before:absolute before:inset-y-1.5 before:-left-2 before:w-1 before:rounded-r before:bg-sidebar-indicator";
const ITEM_IDLE = "text-sidebar-muted hover:bg-sidebar-hover hover:text-sidebar-fg";

function Sidebar({ groups, onNavigate }: { groups: NavGroup[]; onNavigate?: () => void }) {
  const { can, isPlatform } = useSession();
  const loc = useLocation();
  const visible = groups
    .map((g) => ({ ...g, items: g.items.filter((it) => isPlatform || !it.permission || can(it.permission)) }))
    .filter((g) => g.items.length);
  const itemActive = (it: NavItem) => (it.end ? loc.pathname === it.to : loc.pathname === it.to || loc.pathname.startsWith(`${it.to}/`));
  // Accordion: um módulo aberto por vez. O módulo da rota atual abre ao navegar.
  const activeGroupId = visible.find((g) => g.id && g.items.some(itemActive))?.id ?? null;
  const [openId, setOpenId] = React.useState<string | null>(activeGroupId);
  React.useEffect(() => {
    if (activeGroupId) setOpenId(activeGroupId);
  }, [activeGroupId]);
  const toggle = (id: string) => setOpenId((cur) => (cur === id ? null : id));
  const renderItems = (items: NavItem[], nested: boolean) => (
    <ul className={cn("flex flex-col gap-0.5", nested && "ml-4 border-l border-sidebar-border pl-2")}>
      {items.map((it) => (
        <li key={it.to}>
          <NavLink to={it.to} end={it.end} onClick={onNavigate} className={({ isActive }) => cn(ITEM_BASE, isActive ? ITEM_ACTIVE : ITEM_IDLE)}>
            <it.icon className="h-4 w-4 shrink-0" aria-hidden />
            <span className="truncate">{it.label}</span>
          </NavLink>
        </li>
      ))}
    </ul>
  );
  return (
    <nav aria-label="Navegação principal" className="flex flex-col gap-1 px-3 py-4">
      {visible.map((g, i) => {
        // Grupo sem rótulo (ex.: Visão geral) fica achatado no topo, sem submenu.
        if (!g.label || !g.id) return <div key={`flat-${i}`}>{renderItems(g.items, false)}</div>;
        const Icon = g.icon;
        const isActiveGroup = g.items.some(itemActive);
        const expanded = openId === g.id;
        return (
          <div key={g.id}>
            <button
              type="button"
              onClick={() => toggle(g.id!)}
              aria-expanded={expanded}
              className={cn(
                "flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-colors focus-visible:outline-2 focus-visible:outline-sidebar-indicator",
                isActiveGroup ? "font-medium text-sidebar-fg" : "text-sidebar-muted hover:bg-sidebar-hover hover:text-sidebar-fg",
              )}
            >
              {Icon && <Icon className="h-4 w-4 shrink-0" aria-hidden />}
              <span className="flex-1 truncate text-left">{g.label}</span>
              <ChevronDown className={cn("h-4 w-4 shrink-0 text-sidebar-muted transition-transform", expanded && "rotate-180")} aria-hidden />
            </button>
            {expanded && <div className="mt-0.5">{renderItems(g.items, true)}</div>}
          </div>
        );
      })}
    </nav>
  );
}

/** Alternador de tema (claro/escuro) — persiste em localStorage e aplica data-theme no <html>. */
function useTheme(): ["claro" | "escuro", () => void] {
  const [theme, setTheme] = React.useState<"claro" | "escuro">(() => {
    try {
      return localStorage.getItem("cm.theme") === "escuro" ? "escuro" : "claro";
    } catch {
      return "claro";
    }
  });
  React.useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem("cm.theme", theme);
    } catch {
      /* ignora (modo privado) */
    }
  }, [theme]);
  return [theme, () => setTheme((t) => (t === "claro" ? "escuro" : "claro"))];
}

function ThemeToggle() {
  const [theme, toggle] = useTheme();
  const dark = theme === "escuro";
  return (
    <button
      type="button"
      onClick={toggle}
      className="grid h-8 w-8 place-items-center rounded-md text-muted hover:bg-surface-muted hover:text-fg focus-visible:outline-2 focus-visible:outline-focus"
      aria-label={dark ? "Mudar para tema claro" : "Mudar para tema escuro"}
      title={dark ? "Tema claro" : "Tema escuro"}
    >
      {dark ? <Sun className="h-[18px] w-[18px]" aria-hidden /> : <Moon className="h-[18px] w-[18px]" aria-hidden />}
    </button>
  );
}

function WorkspaceSwitcher() {
  const { me, activeWorkspace, switchWorkspace } = useSession();
  if (!me || me.kind !== "user") return null;
  const byOrg = new Map<string, typeof me.workspaces>();
  for (const w of me.workspaces) byOrg.set(w.organization_name, [...(byOrg.get(w.organization_name) ?? []), w]);
  return (
    <Menu>
      <MenuTrigger className="flex min-w-0 max-w-[16rem] items-center gap-2 rounded-md border border-border bg-surface px-2.5 py-1.5 text-left text-sm hover:bg-surface-muted focus-visible:outline-2 focus-visible:outline-focus" aria-label="Trocar workspace">
        <Building2 className="h-4 w-4 shrink-0 text-muted" aria-hidden />
        <span className="min-w-0">
          <span className="block truncate text-[11px] leading-tight text-subtle">{activeWorkspace?.organization_name ?? "Sem organização"}</span>
          <span className="block truncate font-medium leading-tight">{activeWorkspace?.name ?? "Selecione"}</span>
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-muted" aria-hidden />
      </MenuTrigger>
      <MenuContent align="start">
        {[...byOrg.entries()].map(([org, list]) => (
          <React.Fragment key={org}>
            <MenuLabel>{org}</MenuLabel>
            {list.map((w) => (
              <MenuItem key={w.id} onSelect={() => void switchWorkspace(w.id)}>
                <span className={cn("truncate", w.id === activeWorkspace?.id && "font-semibold")}>{w.name}</span>
              </MenuItem>
            ))}
          </React.Fragment>
        ))}
      </MenuContent>
    </Menu>
  );
}

export function Shell({ groups, panel }: { groups: NavGroup[]; panel: "app" | "admin" | "master" }) {
  const { me, logout } = useSession();
  const navigate = useNavigate();
  const brand = useBrand();
  const sidebarTone = brand.theme.sidebar === "light" ? "dark" : "light";
  const [open, setOpen] = React.useState(false);
  const loc = useLocation();
  // Rotas com handle.layout = "fill" usam largura total e altura da viewport (ex.: kanban).
  const fill = useMatches().some((m) => (m.handle as { layout?: string } | undefined)?.layout === "fill");
  React.useEffect(() => setOpen(false), [loc.pathname]);
  const panelLabel = panel === "master" ? "Master" : panel === "admin" ? "Admin da marca" : null;
  return (
    <div className="flex h-full">
      <a href="#conteudo" className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-surface focus:px-3 focus:py-2">
        Pular para o conteúdo
      </a>
      <aside className="hidden w-60 shrink-0 flex-col border-r border-sidebar-border bg-sidebar lg:flex">
        <div className="flex h-14 items-center gap-2 border-b border-sidebar-border px-4">
          <BrandLogo tone={sidebarTone} className="h-6 max-w-[11rem]" />
        </div>
        <div className="flex-1 overflow-y-auto">
          <Sidebar groups={groups} />
        </div>
      </aside>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true" aria-label="Menu">
          <div className="absolute inset-0 bg-black/30" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-72 overflow-y-auto bg-sidebar shadow-lg">
            <div className="flex h-14 items-center justify-between border-b border-sidebar-border px-4">
              <BrandLogo tone={sidebarTone} className="h-6 max-w-[11rem]" />
              <button onClick={() => setOpen(false)} aria-label="Fechar menu" className="rounded p-1 text-sidebar-fg hover:bg-sidebar-hover">
                <X className="h-5 w-5" />
              </button>
            </div>
            <Sidebar groups={groups} onNavigate={() => setOpen(false)} />
          </div>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="relative flex h-14 shrink-0 items-center gap-3 border-b border-border bg-surface px-4">
          <button className="rounded p-1.5 hover:bg-surface-muted lg:hidden" onClick={() => setOpen(true)} aria-label="Abrir menu">
            <MenuIcon className="h-5 w-5" />
          </button>
          {panel === "app" && <WorkspaceSwitcher />}
          {panelLabel && (
            <Badge tone={panel === "master" ? "danger" : "brand"} className="uppercase tracking-wide">
              {panelLabel}
            </Badge>
          )}
          {me?.kind === "support" && (
            <Badge tone="warning">
              <ShieldAlert className="h-3 w-3" aria-hidden /> Sessão de suporte (somente leitura)
            </Badge>
          )}
          <div className="ml-auto flex items-center gap-2">
            <ThemeToggle />
            {panel === "app" && me?.is_brand_admin && (
              <Link to="/admin/overview" className="hidden text-sm text-muted hover:text-fg sm:inline">
                Admin da marca
              </Link>
            )}
            {panel === "admin" && (
              <Link to="/app/overview" className="hidden text-sm text-muted hover:text-fg sm:inline">
                Painel do usuário
              </Link>
            )}
            <Menu>
              <MenuTrigger className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-surface-muted focus-visible:outline-2 focus-visible:outline-focus" aria-label="Menu do usuário">
                <span className="grid h-7 w-7 place-items-center rounded-full bg-surface-muted text-xs font-semibold" aria-hidden>
                  {me?.user.name?.slice(0, 1) ?? "?"}
                </span>
                <span className="hidden max-w-[10rem] truncate md:inline">{me?.user.name}</span>
              </MenuTrigger>
              <MenuContent>
                <MenuLabel>{me?.user.email}</MenuLabel>
                <MenuSeparator />
                {/* Troca de painel também no menu (no celular os links inline ficam ocultos). */}
                {panel === "app" && me?.is_brand_admin && (
                  <MenuItem className="sm:hidden" onSelect={() => navigate("/admin/overview")}>
                    Admin da marca
                  </MenuItem>
                )}
                {panel === "admin" && (
                  <MenuItem className="sm:hidden" onSelect={() => navigate("/app/overview")}>
                    Painel do usuário
                  </MenuItem>
                )}
                <MenuItem onSelect={() => void logout()}>
                  <LogOut className="h-4 w-4" aria-hidden /> Sair
                </MenuItem>
              </MenuContent>
            </Menu>
          </div>
        </header>
        <main id="conteudo" className={cn("relative min-w-0 flex-1", fill ? "flex min-h-0 flex-col overflow-y-auto lg:overflow-hidden" : "overflow-y-auto")}>
          <div className={fill ? "flex min-h-0 w-full flex-1 flex-col px-4 pb-3 pt-5 sm:px-6" : "mx-auto w-full max-w-[1400px] px-4 py-6 sm:px-6"}>
            <React.Suspense fallback={<div className="text-sm text-muted">Carregando…</div>}>
              <Outlet />
            </React.Suspense>
          </div>
        </main>
      </div>
    </div>
  );
}
